import { arc, type Arc } from './arc';
import { intersect } from './intersect';
import { segment, type Segment } from './segment';
import { carrier, endpoints, liesOn, offset, type Shape } from './shape';
import { footOnCarrier } from './touch';
import { extendTo, extensionOf, sideOf, splitAt, type End } from './trim';
import {
  add,
  angleOf,
  dist,
  normalize,
  normalizeDegrees,
  scale,
  sub,
  toRadians,
  type Vec2,
} from './vec2';

/** Why a corner, fillet or chamfer cannot be made, worded for the script that asked. */
export class CornerError extends Error {}

/** Two shapes after a corner edit, and what the edit made between them. */
export interface Cornered<M> {
  readonly a: Shape;
  readonly b: Shape;
  readonly made: M;
}

/** How a chamfer is sized: a distance along each line, or one distance and its angle to `a`. */
export type ChamferSize =
  | { readonly distances: readonly [number, number] }
  | { readonly distance: number; readonly angle: number };

/**
 * `a` and `b` trimmed or extended to end where they cross, each keeping the side `near` is
 * on; of several crossings, the one nearest `near`.
 */
export function corner(a: Shape, b: Shape, near: Vec2): Cornered<Vec2> {
  const at = crossingNear(a, b, near);
  return {
    a: endAt(a, at, keptToward(a, at, near)),
    b: endAt(b, at, keptToward(b, at, near)),
    made: at,
  };
}

/**
 * The arc of `radius` touching both shapes in the corner `near` is in, with both trimmed or
 * extended to its ends.
 */
export function fillet(a: Shape, b: Shape, radius: number, near: Vec2): Cornered<Arc> {
  if (!(radius > 0)) throw new CornerError('a fillet radius must be positive');
  closed(a);
  closed(b);
  const center = nearest(
    sides(a, radius).flatMap((pa) => sides(b, radius).flatMap((pb) => crossings(pa, pb))),
    near,
  );
  if (!center) throw new CornerError('no arc of that radius touches both shapes');
  const ta = footOnCarrier(carrier(a), center);
  const tb = footOnCarrier(carrier(b), center);
  if (!ta || !tb) throw new CornerError('no arc of that radius touches both shapes');
  const from = angleOf(sub(ta, center));
  const to = angleOf(sub(tb, center));
  const made =
    normalizeDegrees(to - from) <= 180
      ? arc(center, radius, from, to)
      : arc(center, radius, to, from);
  return {
    a: endAt(a, ta, keptAwayFrom(a, ta, tb)),
    b: endAt(b, tb, keptAwayFrom(b, tb, ta)),
    made,
  };
}

/** A line across the corner `near` is in, with both lines trimmed to its ends. */
export function chamfer(a: Shape, b: Shape, size: ChamferSize, near: Vec2): Cornered<Segment> {
  if (a.type !== 'line' || b.type !== 'line') throw new CornerError('a chamfer needs two lines');
  const met = corner(a, b, near);
  const along = (shape: Shape) => {
    const [p, q] = endpoints(shape) as [Vec2, Vec2];
    const far = dist(p, met.made) > dist(q, met.made) ? p : q;
    return { far, unit: normalize(sub(far, met.made)), length: dist(far, met.made) };
  };
  const [ra, rb] = [along(met.a), along(met.b)];
  const [da, db] = distancesOf(size, ra.unit, rb.unit);
  if (da > ra.length || db > rb.length) throw new CornerError('the chamfer is longer than a line');
  const pa = add(met.made, scale(ra.unit, da));
  const pb = add(met.made, scale(rb.unit, db));
  return { a: lineFrom(met.a, pa, ra.far), b: lineFrom(met.b, pb, rb.far), made: segment(pa, pb) };
}

function distancesOf(size: ChamferSize, ua: Vec2, ub: Vec2): [number, number] {
  if ('distances' in size) {
    const [da, db] = size.distances;
    if (!(da > 0) || !(db > 0)) throw new CornerError('chamfer distances must be positive');
    return [da, db];
  }
  const { distance, angle } = size;
  if (!(distance > 0)) throw new CornerError('a chamfer distance must be positive');
  const between = Math.acos(Math.max(-1, Math.min(1, ua.x * ub.x + ua.y * ub.y)));
  const third = Math.PI - toRadians(angle) - between;
  if (!(angle > 0) || third <= 0) {
    throw new CornerError('a chamfer angle must leave room for the corner it cuts');
  }
  return [distance, (distance * Math.sin(toRadians(angle))) / Math.sin(third)];
}

/** A line from `start` to `far`, running the way `shape` does. */
function lineFrom(shape: Shape, start: Vec2, far: Vec2): Segment {
  const [first] = endpoints(shape) as [Vec2, Vec2];
  return dist(first, far) < 1e-9 ? segment(far, start) : segment(start, far);
}

function closed(shape: Shape): void {
  if (shape.type === 'circle') {
    throw new CornerError('a circle has no end to trim; draw the part to keep as an arc');
  }
}

function crossingNear(a: Shape, b: Shape, near: Vec2): Vec2 {
  closed(a);
  closed(b);
  const found = nearest(crossings(a, b), near);
  if (!found) throw new CornerError('the shapes never meet, even extended');
  return found;
}

/** Where the shapes' extensions cross. */
function crossings(a: Shape, b: Shape): Vec2[] {
  const [ea, eb] = [extensionOf(a) ?? a, extensionOf(b) ?? b];
  return intersect(ea, eb);
}

/** The shape's copies `distance` to either side, where they exist. */
function sides(shape: Shape, distance: number): Shape[] {
  return [offset(shape, distance), offset(shape, -distance)].filter((s): s is Shape => s !== null);
}

function nearest(points: readonly Vec2[], to: Vec2): Vec2 | null {
  let best: Vec2 | null = null;
  for (const p of points) if (!best || dist(p, to) < dist(best, to)) best = p;
  return best;
}

/** The end of `shape` past `at` to keep: the side `toward` falls on. */
function keptToward(shape: Shape, at: Vec2, toward: Vec2): End {
  return sideOf(shape, at, toward);
}

/** The end of `shape` past `at` to keep: the side `away` does not fall on. */
function keptAwayFrom(shape: Shape, at: Vec2, away: Vec2): End {
  return sideOf(shape, at, away) === 'start' ? 'end' : 'start';
}

/** `shape` ending at `at`: cut there keeping `keep`, or lengthened to reach it. */
function endAt(shape: Shape, at: Vec2, keep: End): Shape {
  if (liesOn(shape, at)) return splitAt(shape, at)?.[keep] ?? shape;
  return extendTo(shape, at) ?? shape;
}
